import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import InputIcon from "@mui/icons-material/Input";
import ScaleIcon from "@mui/icons-material/Scale";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import KpiCard from "./KpiCard";
import StatusBadge from "./StatusBadge";
import ReportFilters from "./ReportFilters";
import ExportButtons from "./ExportButtons";
import { exportToPDF, exportToExcel } from "../../../utils/exportUtils";
import RelatorioDistribuidorService from "../../../service/relatorio_distribuidor_service";
import { toLocalISODate } from "../../../utils/dateUtils";

const statusLabels = {
	recebido: "Recebido",
	em_analise: "Em Análise",
	aprovado: "Aprovado",
	rejeitado: "Rejeitado",
};

function getDefaultDates() {
	const hoje = new Date();
	const inicio = new Date(hoje);
	inicio.setDate(inicio.getDate() - 30);
	return { from: toLocalISODate(inicio), to: toLocalISODate(hoje) };
}

function ReportEntradas() {
	const defaults = getDefaultDates();
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("todos");
	const [fornecedorFilter, setFornecedorFilter] = useState("todos");
	const [dateFrom, setDateFrom] = useState(defaults.from);
	const [dateTo, setDateTo] = useState(defaults.to);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);

	useEffect(() => {
		loadData();
	}, [dateFrom, dateTo, statusFilter, fornecedorFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadData = async () => {
		try {
			setLoading(true);
			setError(null);
			const service = new RelatorioDistribuidorService();
			const result = await service.fetchEntradas(dateFrom, dateTo, statusFilter, fornecedorFilter, search);
			setData(result);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const entradas = data?.entradas ?? [];
	const fornecedores = data?.fornecedores ?? [];

	const totalKg = entradas.reduce((s, e) => s + e.pesoKg, 0);
	const totalItens = entradas.length;
	const fornecedoresUnicos = new Set(entradas.map((e) => e.fornecedor.id)).size;
	const aprovados = entradas.filter((e) => e.status === "aprovado").length;

	const getExportConfig = () => ({
		title: "Relatório de Entradas de Produtos",
		subtitle: [
			`Período: ${new Date(dateFrom + "T12:00:00").toLocaleDateString("pt-BR")} a ${new Date(dateTo + "T12:00:00").toLocaleDateString("pt-BR")}`,
			fornecedorFilter !== "todos" ? `Fornecedor: ${fornecedores.find(f => f.id === fornecedorFilter)?.nome}` : "",
		].filter(Boolean).join(" | "),
		filename: `entradas-${dateFrom}-a-${dateTo}`,
		kpis: [
			{ label: "Total Entradas", value: String(totalItens) },
			{ label: "Peso Total", value: `${totalKg.toLocaleString("pt-BR")} kg` },
			{ label: "Fornecedores", value: String(fornecedoresUnicos) },
			{ label: "Taxa Aprovação", value: totalItens ? `${Math.round((aprovados / totalItens) * 100)}%` : "0%" },
		],
		headers: ["Data", "Fornecedor", "Cidade/UF", "Produto", "Variedade", "Lote", "Qtd", "Unidade", "Peso (kg)", "NF", "Status"],
		rows: entradas.map((e) => [
			new Date(e.data + "T12:00:00").toLocaleDateString("pt-BR"),
			e.fornecedor.nome,
			`${e.fornecedor.cidade}/${e.fornecedor.estado}`,
			e.produto,
			e.variedade,
			e.lote,
			e.quantidade,
			e.unidade,
			e.pesoKg,
			e.notaFiscal,
			statusLabels[e.status] || e.status,
		]),
	});

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<InputIcon />} title="Total Entradas" value={String(totalItens)} subtitle="registros no período" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<ScaleIcon />} title="Peso Total" value={`${totalKg.toLocaleString("pt-BR")} kg`} />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<LocalShippingIcon />} title="Fornecedores" value={String(fornecedoresUnicos)} subtitle="fornecedores ativos" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<FactCheckIcon />} title="Taxa Aprovação" value={totalItens ? `${Math.round((aprovados / totalItens) * 100)}%` : "0%"} trend={totalItens > 0 && aprovados / totalItens > 0.8 ? "up" : "down"} trendValue={`${aprovados} de ${totalItens}`} />
				</Grid>
			</Grid>

			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
				<ReportFilters
					searchValue={search}
					onSearchChange={setSearch}
					searchPlaceholder="Produto, fornecedor ou lote..."
					dateFrom={dateFrom}
					dateTo={dateTo}
					onDateFromChange={setDateFrom}
					onDateToChange={setDateTo}
					extraFilters={[
						{
							label: "Status",
							value: statusFilter,
							onChange: setStatusFilter,
							options: [
								{ value: "recebido", label: "Recebido" },
								{ value: "em_analise", label: "Em Análise" },
								{ value: "aprovado", label: "Aprovado" },
								{ value: "rejeitado", label: "Rejeitado" },
							],
						},
						{
							label: "Fornecedor",
							value: fornecedorFilter,
							onChange: setFornecedorFilter,
							options: fornecedores.map((f) => ({ value: f.id, label: f.nome })),
						},
					]}
				/>
				<ExportButtons onExportPDF={() => exportToPDF(getExportConfig())} onExportExcel={() => exportToExcel(getExportConfig())} />
			</Box>

			<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
				<CardContent sx={{ p: 0 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", fontSize: "0.9rem", px: 2.5, pt: 2, pb: 1.5 }}>
						Entradas de Produtos
					</Typography>
					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4, gap: 1, color: "#8e9bae" }}>
							<CircularProgress size={18} /> <Typography variant="body2">Carregando...</Typography>
						</Box>
					) : error ? (
						<Box sx={{ py: 4, textAlign: "center", color: "#dc2626" }}><Typography variant="body2">Erro ao carregar dados</Typography></Box>
					) : (
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Data</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Fornecedor</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Produto</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Lote</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Qtd</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Peso (kg)</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>NF</TableCell>
										<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Status</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{entradas.map((e) => (
										<TableRow key={e.id}>
											<TableCell sx={{ fontSize: "0.8rem" }}>{new Date(e.data + "T12:00:00").toLocaleDateString("pt-BR")}</TableCell>
											<TableCell>
												<Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#0f4557" }}>{e.fornecedor.nome}</Typography>
												<Typography variant="caption" sx={{ color: "#8e9bae", fontSize: "0.7rem" }}>{e.fornecedor.cidade}/{e.fornecedor.estado}</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{e.produto}</Typography>
												<Typography variant="caption" sx={{ color: "#8e9bae", fontSize: "0.7rem" }}>{e.variedade}</Typography>
											</TableCell>
											<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{e.lote}</TableCell>
											<TableCell align="right" sx={{ fontSize: "0.8rem" }}>{e.quantidade} {e.unidade}</TableCell>
											<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{e.pesoKg.toLocaleString("pt-BR")}</TableCell>
											<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{e.notaFiscal}</TableCell>
											<TableCell><StatusBadge status={e.status} /></TableCell>
										</TableRow>
									))}
									{entradas.length === 0 && (
										<TableRow>
											<TableCell colSpan={8} sx={{ textAlign: "center", color: "#8e9bae", py: 4 }}>Nenhum registro encontrado</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardContent>
			</Card>
		</Box>
	);
}

export default ReportEntradas;
